import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { TASK_BOARD_DONE_MAX, type TaskBoardMeta, type TaskCard, type TaskDetail, type TaskNotification, type TaskTagCount } from '@shared/types/tasks';
import { usePermission } from '@/hooks/usePermission';
import { useLoader } from '@/hooks/use-loader';
import { loaderStore } from '@/hooks/loader-store';
import type { ApiResponse } from '@/lib/api-contract';
import { useCurrentProject } from '@/lib/current-project';
import { TASK_META_STALE_MS, taskMetaKey } from '@/lib/task-meta';
import { forgetTaskDetail, rememberTaskDetail, taskDetailIsFresh, taskDetailKey, type CachedTaskDetail } from '@/lib/task-detail-cache';
import { getTask, getTaskMeta, getTaskNotifications } from '@/lib/tasks-api';
import { boardKey, cardInScope, useTaskStore, type BoardQuery } from '@/stores/taskStore';
import { applyToBoard, applyToDetail, useTaskOptimistic } from '@/stores/taskOptimistic';

export const LIVE_DEBOUNCE_MS = 250;

/** Refetch this task everywhere it's shown (works even when the live socket is down). */
export function touchTask(taskId: string) {
  useTaskStore.getState().receive({ type: 'task', task_id: taskId, actor_user_id: null });
}

/** Board (or a Tasks panel's scoped board), refreshed whenever the live socket reports a change (one task's: just its card). */
export function useTaskBoard(query: BoardQuery, enabled = true) {
  const key = boardKey(query);
  const entry = useTaskStore((s) => s.boards[key]);
  const optimistic = useTaskOptimistic();
  const boardTick = useTaskStore((s) => s.boardTick);
  const loadBoard = useTaskStore((s) => s.loadBoard);
  // The latest query for `reload`, which callers may hold on to between renders.
  const queryRef = useRef(query);
  useLayoutEffect(() => {
    queryRef.current = query;
  });

  const reload = useCallback(() => loadBoard(queryRef.current), [loadBoard]);
  const loadOlderDone = useTaskStore((s) => s.loadOlderDone);
  const searchFinished = useTaskStore((s) => s.searchFinished);
  /** Show older: the next finished tasks into this board. */
  const loadOlder = useCallback(() => loadOlderDone(queryRef.current), [loadOlderDone]);
  /** The board's search also finds older finished tasks it hasn't loaded. */
  const searchOlder = useCallback((q: string) => searchFinished(queryRef.current, q), [searchFinished]);

  useEffect(() => {
    if (enabled) void reload();
  }, [key, enabled, reload]);

  const firstTick = useRef(boardTick);
  useEffect(() => {
    if (!enabled || boardTick === firstTick.current) return undefined;
    const timer = setTimeout(() => void reload(), LIVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [boardTick, enabled, reload]);

  // Pending changes and just-created tasks show on top of what was fetched.
  const board = useMemo(() => {
    if (!entry?.data) return null;
    const { scope, archived } = entry.query;
    return applyToBoard(
      entry.data,
      entry.fetchedFrom,
      optimistic,
      (card) => cardInScope(card, scope) && (archived || !card.archived),
      entry.cardsFetchedFrom
    );
  }, [entry, optimistic]);

  return {
    key,
    board,
    loading: entry?.loading ?? enabled,
    error: entry?.error ?? null,
    reload,
    /** Finished tasks not loaded yet (the Board only; absent for a Tasks panel's board, and once Show older is at its limit). */
    olderDone: entry?.data?.older_done && entry.doneLimit < TASK_BOARD_DONE_MAX ? entry.data.older_done : null,
    loadingOlder: entry?.loadingOlder ?? false,
    loadOlder,
    /** The search for older finished tasks failed (Retry sends it again). */
    searchError: entry?.search.error ?? null,
    /** The search found more older tasks than it sent (only the latest show). */
    searchMore: entry?.search.more ?? false,
    /** Changes with each full fetch of this board, after which its search is sent again. */
    fetchedFrom: entry?.fetchedFrom ?? 0,
    searchOlder,
  };
}

function seedDetail(card: TaskCard, seed: Partial<TaskDetail> = {}): TaskDetail {
  // Until it loads, the version is when the task was made (its description's version before any save).
  return {
    ...card,
    description: '',
    description_updated_at: card.created_at,
    spec: '',
    spec_updated_at: card.created_at,
    following: true,
    events: [],
    attachments: [],
    subtask_cards: [],
    ...seed,
  };
}

/** Nothing to listen to (no project, or no task open). */
const listenToNothing = () => () => undefined;

/**
 * One task with its timeline; refetches when that task changes anywhere. Until the server
 * answers it shows the copy loaded ahead (SF-T-236: the Inbox's next task, lib/task-detail-cache)
 * or else the card already on the Board (or the task being created), so opening a task is instant;
 * `hydrated` says whether the description and timeline have loaded.
 */
export function useTaskDetail(taskId: string | null) {
  // Both remember which task they're for, so switching tasks needs no reset: a result for
  // another task is simply ignored.
  const [server, setServer] = useState<{ task: TaskDetail; fetchedFrom: number } | null>(null);
  const [failure, setFailure] = useState<{ taskId: string; message: string; status?: number } | null>(null);
  const tick = useTaskStore((s) => (taskId ? s.taskTicks[taskId] ?? 0 : 0));
  const boards = useTaskStore((s) => s.boards);
  const optimistic = useTaskOptimistic();
  const latest = useRef(taskId);
  // The tab's project: a task's cached copy is kept under it.
  const projectId = useCurrentProject()?.id;
  const latestProject = useRef(projectId);
  useLayoutEffect(() => {
    latest.current = taskId;
    latestProject.current = projectId;
  });
  const cacheKey = projectId && taskId ? taskDetailKey(projectId, taskId) : null;
  const listen = useCallback((onChange: () => void) => (cacheKey ? loaderStore.listen(cacheKey, onChange) : listenToNothing()), [cacheKey]);
  const readCache = () => (cacheKey ? loaderStore.snapshot<CachedTaskDetail>(cacheKey).data : undefined);
  const cached = useSyncExternalStore(listen, readCache, readCache);

  // State is only set once the request answers (and only if that task is still the one shown).
  const load = useCallback((id: string) => {
    const startedAt = Date.now();
    // True when the task arrived (callers like Reload keep their draft otherwise).
    return getTask(id).then((res) => {
      if (latest.current !== id) return false;
      if (res.data) {
        setServer({ task: res.data.task, fetchedFrom: startedAt });
        rememberTaskDetail(latestProject.current, { task: res.data.task, fetchedFrom: startedAt });
        setFailure(null);
        return true;
      }
      // Deleted (or never there): its copy loaded ahead mustn't keep showing it as if it existed.
      if (res.status === 404) forgetTaskDetail(latestProject.current, id);
      setFailure({ taskId: id, message: res.error ?? 'Could not load the task', status: res.status });
      return false;
    });
  }, []);
  const reload = useCallback(async () => (taskId ? load(taskId) : false), [taskId, load]);

  useEffect(() => {
    // A copy loaded ahead seconds ago is shown as it is (stepping quickly through the Inbox, SF-T-236).
    if (taskId && !taskDetailIsFresh(latestProject.current, taskId)) void load(taskId);
  }, [taskId, load]);

  const firstTick = useRef(tick);
  useEffect(() => {
    if (!taskId || tick === firstTick.current) return undefined;
    const timer = setTimeout(() => void load(taskId), LIVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [tick, taskId, load]);

  // This task as the server sent it: the newer of this view's own answer and the cached copy.
  const own = server?.task.id === taskId ? server : null;
  const gone = failure?.taskId === taskId && failure.status === 404;
  const ahead = cached?.task.id === taskId && !gone ? cached : null;
  const loaded = own && (!ahead || own.fetchedFrom >= ahead.fetchedFrom) ? own : ahead;

  const seed = useMemo(() => {
    if (!taskId || loaded) return null;
    const created = optimistic.newTasks[taskId];
    if (created) return seedDetail(created.card, created.seed);
    for (const entry of Object.values(boards)) {
      const card = entry.data?.tasks.find((t) => t.id === taskId);
      if (card) return seedDetail(card);
    }
    return null;
  }, [taskId, loaded, boards, optimistic.newTasks]);

  const hydrated = Boolean(loaded);
  const task = useMemo(() => {
    const base = loaded ? loaded.task : seed;
    return base ? applyToDetail(base, loaded ? loaded.fetchedFrom : 0, optimistic) : null;
  }, [loaded, seed, optimistic]);

  const failed = !task && failure?.taskId === taskId ? failure : null;
  // `notFound`: the server answered 404 (deleted, or a wrong link), not a failure to retry.
  return { task, hydrated, error: failed?.message ?? null, notFound: failed?.status === 404, reload };
}

export function useTaskNotifications(enabled: boolean) {
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const tick = useTaskStore((s) => s.notificationTick);

  const reload = useCallback(async () => {
    const res = await getTaskNotifications();
    if (res.data) {
      setNotifications(res.data.notifications);
      setUnread(res.data.unread);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setTimeout(() => void reload(), tick === 0 ? 0 : LIVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [enabled, tick, reload]);

  return useMemo(() => ({ notifications, unread, reload, setNotifications, setUnread }), [notifications, unread, reload]);
}

/**
 * A project's columns, labels, types, people and `#tags` (lib/task-meta.ts): shown at once from the
 * cache, loaded again behind it when older than a minute. `projectId` defaults to the tab's project.
 * For the tab's project, a board-wide live change (columns, labels, types; a reconnect) loads it again
 * quietly while it's in use. A task change doesn't: its card refresh brings the tag counts and people
 * into the cache (rememberLiveMeta), and the rest waits for the copy to grow old.
 *
 * `data` is only ever this project's: while another project's loads, the loader would keep showing
 * the last one's (its placeholder), and a form could offer that project's columns for this one.
 */
export function useTaskMeta(enabled = true, projectId?: string) {
  const current = useCurrentProject();
  const project = projectId ?? current?.id;
  const on = enabled && Boolean(project);
  const loaded = useLoader((signal) => getTaskMeta({ project, signal }), [project], {
    key: project ? taskMetaKey(project) : undefined,
    staleMs: TASK_META_STALE_MS,
    enabled: on,
  });
  // The live socket is the tab's project's only.
  const live = on && project === current?.id;
  const tick = useTaskStore((s) => s.boardTick);
  const [seenTick, setSeenTick] = useState(tick);
  const refresh = loaded.refresh;
  useEffect(() => {
    if (!live || tick === seenTick) return undefined;
    const timer = setTimeout(() => {
      setSeenTick(tick);
      void refresh().catch(() => undefined);
    }, LIVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [live, tick, seenTick, refresh]);
  // The loader re-renders when its key's entry changes, so reading that entry here stays current.
  const own = project ? loaderStore.snapshot<ApiResponse<TaskBoardMeta>>(taskMetaKey(project)) : null;
  const data = own?.hasData ? own.data : undefined;
  return useMemo(() => ({ ...loaded, data }), [loaded, data]);
}

const NO_TAGS: TaskTagCount[] = [];

/** The project's `#tags`, most used first; loaded only once `enabled` (someone typed `#`), then cached. */
export function useProjectTags(enabled: boolean): TaskTagCount[] {
  const canView = usePermission('PROJECT_VIEW');
  const meta = useTaskMeta(enabled && canView);
  return meta.data?.data?.tags ?? NO_TAGS;
}
